/**
 * Permission catalog (§7.2). Permissions are persisted in the `permissions` table and
 * mapped to roles in `role_permissions`; this catalog is only the seed source and the
 * shared vocabulary for guards and UI gating. Authorization decisions always read the DB.
 *
 * `scoped: true` permissions are constrained by the organizational scope of the role
 * assignment that grants them (INSTITUTION → everything, COLLEGE → that college's
 * departments, DEPARTMENT → that department). Unscoped permissions are global.
 */
export interface PermissionDefinition {
  readonly code: string;
  readonly category: string;
  readonly description: string;
  readonly scoped: boolean;
}

const p = (code: string, category: string, description: string, scoped = false) =>
  ({ code, category, description, scoped }) as const;

export const PERMISSION_DEFINITIONS = [
  // Reference data
  p('reference.read', 'reference', 'Read organization and academic reference data'),

  // Users, roles and permissions
  p('user.read', 'users', 'View user accounts'),
  p('user.manage', 'users', 'Create, update, deactivate and unlock user accounts; revoke sessions'),
  p('role.read', 'users', 'View roles and permission mappings'),
  p('role.manage', 'users', 'Manage roles, role permissions and role assignments'),

  // Organization and academic master data
  p('org.manage', 'organization', 'Manage institution, campuses, buildings, colleges, departments and programs'),
  p('location.manage', 'organization', 'Manage locations and rooms'),
  p('geofence.manage', 'organization', 'Manage geofences'),
  p('academic.manage', 'academic', 'Manage academic years, terms, courses and sections'),

  // Faculty
  p('faculty.read.self', 'faculty', 'View own faculty profile'),
  p('faculty.read.department', 'faculty', 'View faculty profiles within organizational scope', true),
  p('faculty.manage', 'faculty', 'Create and update faculty profiles within organizational scope', true),
  p('employment_type.manage', 'faculty', 'Configure employment classifications'),

  // Scheduling
  p('schedule.read.self', 'schedule', 'View own teaching and duty schedule'),
  p('schedule.read.department', 'schedule', 'View schedules within organizational scope', true),
  p('schedule.manage', 'schedule', 'Create, edit, duplicate and deactivate schedules within scope', true),
  p('schedule.import', 'schedule', 'Import schedules from XLSX/CSV'),
  p('faculty_load.manage', 'schedule', 'Manage faculty load assignments within scope', true),

  // Attendance
  p('attendance.clock_in', 'attendance', 'Clock in to own scheduled session or duty'),
  p('attendance.clock_out', 'attendance', 'Clock out of own active session'),
  p('attendance.read.self', 'attendance', 'View own attendance history and hours'),
  p('attendance.read.department', 'attendance', 'View attendance within organizational scope', true),
  p('attendance.location.read', 'attendance', 'View raw GPS coordinates captured at clock events', true),
  p('attendance.verify', 'attendance', 'Verify attendance and review exceptions within scope', true),
  p('attendance.approve', 'attendance', 'Approve verified attendance within scope', true),
  p('attendance.finalize', 'attendance', 'HR finalization of attendance for payroll cut-off', true),
  p('attendance.certify', 'attendance', 'Certify own attendance record before cut-off'),
  p('attendance.record_manual', 'attendance', 'Record manually approved attendance on behalf of faculty', true),
  p('attendance.correct.request', 'attendance', 'Submit attendance correction requests for own records'),
  p('attendance.correct.approve', 'attendance', 'Approve or reject attendance corrections within scope', true),
  p('attendance.post_lock_adjust', 'attendance', 'Privileged post-lock/post-processing adjustment', true),

  // Devices (§37 optional device registration, Phase 2B)
  p('device.register_self', 'devices', 'Register and remove own devices for attendance'),
  p('device.read', 'devices', 'View devices registered by users'),
  p('device.manage', 'devices', 'Approve and revoke registered devices'),
  // Attendance displays showing room codes (§37 dynamic QR, Phase 2E)
  p('display.read', 'devices', 'View attendance displays (room and kiosk screens)'),
  p('display.manage', 'devices', 'Set up, pair and remove attendance displays and NFC room tags'),
  // ID cards tapped on kiosk card readers (Phase 3G)
  p('card.read', 'devices', 'View the ID cards enrolled for clocking in'),
  p('card.manage', 'devices', 'Enrol and revoke ID cards for clocking in'),

  // Institutional events (§8.1, §67, Phase 2E)
  p('event.check_in', 'events', 'Check in to institutional events one is invited to'),
  p('event.read', 'events', 'View institutional events and their attendance within organizational scope', true),
  p('event.manage', 'events', 'Organize institutional events and record their attendance within organizational scope', true),

  // Proof and progress
  p('proof.upload', 'proof', 'Upload class proof and session progress for own sessions'),
  p('proof.verify', 'proof', 'Review and verify class proof within scope', true),

  // Academic exceptions
  p('makeup.request', 'makeup', 'Request makeup classes for own schedules'),
  p('makeup.approve', 'makeup', 'Approve or reject makeup requests within scope', true),
  p('substitution.request', 'substitution', 'Request substitute faculty'),
  p('substitution.approve', 'substitution', 'Approve or reject substitutions within scope', true),
  p('overload.request', 'overload', 'Request overload or extra-load assignments'),
  p('overload.approve', 'overload', 'Approve or reject overload/extra-load within scope', true),
  p('duty.request', 'duty', 'Log administrative or non-teaching duty'),
  p('duty.approve', 'duty', 'Approve administrative duties within scope', true),
  p('duty.manage', 'duty', 'Assign administrative duties and configure duty categories', true),
  p('leave.request', 'leave', 'File own leave requests'),
  p('leave.approve', 'leave', 'Approve or reject leave within scope', true),
  p('leave.manage', 'leave', 'Record and manage leave on behalf of faculty', true),
  p('leave.sync', 'leave', 'Exchange leave with the HR information system: record leave approved there, receive leave decided here (integration clients)', true),
  p('attendance.time_clock_sync', 'attendance', 'Send punches of registered biometric time clocks; they are always verified by a person (integration clients)'),
  p('attendance.meeting_sync', 'attendance', 'Send meeting-platform join and leave events of faculty members as evidence for their online classes (integration clients)'),
  p('calendar.manage', 'calendar', 'Manage holidays, suspensions and institutional calendar'),

  // Policy, workflow and settings
  p('policy.manage', 'settings', 'Manage attendance policies and rules'),
  p('workflow.manage', 'settings', 'Configure approval workflows'),
  p('settings.manage', 'settings', 'Manage system settings'),

  // Payroll support
  p('payroll_period.manage', 'payroll', 'Create, review, submit and lock payroll cut-offs'),
  p('payroll_period.reopen', 'payroll', 'Reopen a locked payroll cut-off with authorization'),
  p('payroll_report.read', 'payroll', 'View payroll-ready attendance reports'),
  p('payroll_report.export', 'payroll', 'Export payroll-ready reports (XLSX/CSV/PDF)'),
  p('payroll_report.mark_processed', 'payroll', 'Mark payroll report processed and enter payroll reference'),

  // Reporting and dashboards
  p('report.read.self', 'reports', 'Download own DTR and teaching-hour reports'),
  p('report.read.department', 'reports', 'View attendance reports within organizational scope', true),
  p('report.export', 'reports', 'Export attendance reports within organizational scope', true),
  p('dashboard.read.department', 'reports', 'View real-time attendance dashboard within scope', true),

  // Audit and operations
  p('audit.read', 'audit', 'Read audit logs'),
  p('system.health.read', 'audit', 'View technical health and operational information'),

  // Integrations (§7.1 "Manage integrations", §68, Phase 3A)
  p('integration.read', 'integrations', 'View integration clients, webhooks, deliveries and the integration event log'),
  p('integration.manage', 'integrations', 'Create, change and disable integration clients and their secrets; manage webhooks'),
] as const satisfies readonly PermissionDefinition[];

export type PermissionCode = (typeof PERMISSION_DEFINITIONS)[number]['code'];

export const PERMISSION_CODES = PERMISSION_DEFINITIONS.map((d) => d.code) as readonly PermissionCode[];

const SCOPED = new Set<string>(PERMISSION_DEFINITIONS.filter((d) => d.scoped).map((d) => d.code));

export function isScopedPermission(code: string): boolean {
  return SCOPED.has(code);
}

/** Permissions a workflow step may require of its approvers (§64). */
export const WORKFLOW_APPROVER_PERMISSIONS = [
  'attendance.correct.approve',
  'attendance.verify',
  'attendance.approve',
  'attendance.finalize',
  'makeup.approve',
  'substitution.approve',
  'overload.approve',
  'duty.approve',
  'leave.approve',
] as const satisfies readonly PermissionCode[];
export type WorkflowApproverPermission = (typeof WORKFLOW_APPROVER_PERMISSIONS)[number];

/** Typed permission constants, e.g. `Permissions.FACULTY_MANAGE === 'faculty.manage'`. */
export const Permissions = {
  REFERENCE_READ: 'reference.read',
  USER_READ: 'user.read',
  USER_MANAGE: 'user.manage',
  ROLE_READ: 'role.read',
  ROLE_MANAGE: 'role.manage',
  ORG_MANAGE: 'org.manage',
  LOCATION_MANAGE: 'location.manage',
  GEOFENCE_MANAGE: 'geofence.manage',
  ACADEMIC_MANAGE: 'academic.manage',
  FACULTY_READ_SELF: 'faculty.read.self',
  FACULTY_READ_DEPARTMENT: 'faculty.read.department',
  FACULTY_MANAGE: 'faculty.manage',
  EMPLOYMENT_TYPE_MANAGE: 'employment_type.manage',
  SCHEDULE_READ_SELF: 'schedule.read.self',
  SCHEDULE_READ_DEPARTMENT: 'schedule.read.department',
  SCHEDULE_MANAGE: 'schedule.manage',
  SCHEDULE_IMPORT: 'schedule.import',
  FACULTY_LOAD_MANAGE: 'faculty_load.manage',
  ATTENDANCE_CLOCK_IN: 'attendance.clock_in',
  ATTENDANCE_CLOCK_OUT: 'attendance.clock_out',
  ATTENDANCE_READ_SELF: 'attendance.read.self',
  ATTENDANCE_READ_DEPARTMENT: 'attendance.read.department',
  ATTENDANCE_LOCATION_READ: 'attendance.location.read',
  ATTENDANCE_VERIFY: 'attendance.verify',
  ATTENDANCE_APPROVE: 'attendance.approve',
  ATTENDANCE_FINALIZE: 'attendance.finalize',
  ATTENDANCE_CERTIFY: 'attendance.certify',
  ATTENDANCE_RECORD_MANUAL: 'attendance.record_manual',
  ATTENDANCE_CORRECT_REQUEST: 'attendance.correct.request',
  ATTENDANCE_CORRECT_APPROVE: 'attendance.correct.approve',
  ATTENDANCE_POST_LOCK_ADJUST: 'attendance.post_lock_adjust',
  DEVICE_REGISTER_SELF: 'device.register_self',
  DEVICE_READ: 'device.read',
  DEVICE_MANAGE: 'device.manage',
  DISPLAY_READ: 'display.read',
  DISPLAY_MANAGE: 'display.manage',
  CARD_READ: 'card.read',
  CARD_MANAGE: 'card.manage',
  EVENT_CHECK_IN: 'event.check_in',
  EVENT_READ: 'event.read',
  EVENT_MANAGE: 'event.manage',
  PROOF_UPLOAD: 'proof.upload',
  PROOF_VERIFY: 'proof.verify',
  MAKEUP_REQUEST: 'makeup.request',
  MAKEUP_APPROVE: 'makeup.approve',
  SUBSTITUTION_REQUEST: 'substitution.request',
  SUBSTITUTION_APPROVE: 'substitution.approve',
  OVERLOAD_REQUEST: 'overload.request',
  OVERLOAD_APPROVE: 'overload.approve',
  DUTY_REQUEST: 'duty.request',
  DUTY_APPROVE: 'duty.approve',
  DUTY_MANAGE: 'duty.manage',
  LEAVE_REQUEST: 'leave.request',
  LEAVE_APPROVE: 'leave.approve',
  LEAVE_MANAGE: 'leave.manage',
  LEAVE_SYNC: 'leave.sync',
  ATTENDANCE_TIME_CLOCK_SYNC: 'attendance.time_clock_sync',
  ATTENDANCE_MEETING_SYNC: 'attendance.meeting_sync',
  CALENDAR_MANAGE: 'calendar.manage',
  POLICY_MANAGE: 'policy.manage',
  WORKFLOW_MANAGE: 'workflow.manage',
  SETTINGS_MANAGE: 'settings.manage',
  PAYROLL_PERIOD_MANAGE: 'payroll_period.manage',
  PAYROLL_PERIOD_REOPEN: 'payroll_period.reopen',
  PAYROLL_REPORT_READ: 'payroll_report.read',
  PAYROLL_REPORT_EXPORT: 'payroll_report.export',
  PAYROLL_REPORT_MARK_PROCESSED: 'payroll_report.mark_processed',
  REPORT_READ_SELF: 'report.read.self',
  REPORT_READ_DEPARTMENT: 'report.read.department',
  REPORT_EXPORT: 'report.export',
  DASHBOARD_READ_DEPARTMENT: 'dashboard.read.department',
  AUDIT_READ: 'audit.read',
  SYSTEM_HEALTH_READ: 'system.health.read',
  INTEGRATION_READ: 'integration.read',
  INTEGRATION_MANAGE: 'integration.manage',
} as const satisfies Record<string, PermissionCode>;

/**
 * Permissions an integration client can use (Phase 3A, ADR-054): reading reference and
 * attendance data, maintaining the master data an SIS or HRIS owns, the payroll hand-off,
 * the leave exchange with an HRIS (leave that people approved there), the punches of registered
 * time clocks (Phase 3H, recorded only for verification) and meeting-platform events as evidence
 * for online classes (Phase 3I, never a clock event). A client's roles may grant
 * more — account, role, setting and workflow administration, audit, approvals, verification,
 * clocking — but those never reach a client: every decision about a person's attendance stays
 * with a person.
 */
export const INTEGRATION_PERMISSIONS = [
  'reference.read',
  'org.manage',
  'location.manage',
  'academic.manage',
  'faculty.read.department',
  'faculty.manage',
  'employment_type.manage',
  'schedule.read.department',
  'schedule.manage',
  'schedule.import',
  'faculty_load.manage',
  'leave.sync',
  'attendance.time_clock_sync',
  'attendance.meeting_sync',
  'calendar.manage',
  'attendance.read.department',
  'event.read',
  'report.read.department',
  'report.export',
  'payroll_report.read',
  'payroll_report.export',
  'payroll_report.mark_processed',
] as const satisfies readonly PermissionCode[];
export type IntegrationPermission = (typeof INTEGRATION_PERMISSIONS)[number];

const INTEGRATION_SET = new Set<string>(INTEGRATION_PERMISSIONS);

export function isIntegrationPermission(code: string): code is IntegrationPermission {
  return INTEGRATION_SET.has(code);
}
